import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import RichTextEditor from './RichTextEditor';
import { toast } from 'react-hot-toast';

/**
 * ActiveSessionEditor Component
 *
 * TipTap editor for writing session notes during active GM Screen session.
 * Features:
 * - Auto-saves to Firestore: campaigns/{id}/currentSessionDraft
 * - Supports GM-only syntax (//) with visual highlighting
 * - Loads existing drafts or starts blank
 * - Exposes forceSave() method via ref
 *
 * @param {string} campaignId - Campaign ID for Firestore path
 * @param {string} userId - User ID (GM)
 * @param {React.Ref} ref - Forward ref for forceSave() method
 */
const ActiveSessionEditor = forwardRef(({ campaignId, userId }, ref) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  // Load existing draft or create from template on mount
  useEffect(() => {
    const loadDraft = async () => {
      if (!campaignId) {
        console.warn('[ActiveSessionEditor] No campaignId provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Try to load existing draft from Firestore
        const draftRef = doc(db, 'campaigns', campaignId, 'currentSessionDraft', 'draft');
        const draftSnap = await getDoc(draftRef);

        if (draftSnap.exists()) {
          // Load existing draft
          const draftData = draftSnap.data();
          setContent(draftData.content || '');
          setLastSaved(draftData.lastSaved?.toDate() || null);
        } else {
          // No draft exists - start blank
          setContent('');
        }
      } catch (error) {
        console.error('Error loading session notes:', error);
        toast.error('Failed to load session notes');
        setContent('');
      } finally {
        setIsLoading(false);
      }
    };

    loadDraft();
  }, [campaignId]);

  // Save draft to Firestore
  const handleSave = useCallback(async (html) => {
    if (!campaignId) return;

    try {
      const draftRef = doc(db, 'campaigns', campaignId, 'currentSessionDraft', 'draft');

      await setDoc(draftRef, {
        content: html,
        lastSaved: new Date(),
        updatedBy: userId
      }, { merge: true });

      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving session notes:', error);
      throw error; // Propagate to RichTextEditor for retry logic
    }
  }, [campaignId, userId]);

  // Handle content changes
  const handleChange = useCallback((html) => {
    setContent(html);
  }, []);

  // Expose force save method to parent via ref
  useImperativeHandle(ref, () => ({
    forceSave: async () => {
      if (!content.trim()) return null;
      await handleSave(content);
      return content;
    }
  }), [content, handleSave]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400 text-sm">Loading session notes...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <RichTextEditor
        content={content}
        onChange={handleChange}
        onSave={handleSave}
        isGM={true}
        placeholder="Write your session notes here..."
        autoSave={true}
        autoSaveDelay={30000} // 30 seconds
      />

      {lastSaved && (
        <div className="text-xs text-gray-500 mt-2 text-right">
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
});

ActiveSessionEditor.displayName = 'ActiveSessionEditor';

export default ActiveSessionEditor;
